import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { CircleNotch, GithubLogo } from '@phosphor-icons/react';
import { FcGoogle } from 'react-icons/fc';
import { FaDiscord, FaSpotify } from 'react-icons/fa';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface GuestConversionContextType {
  /** Abre o dialog de conversao de conta. */
  open_conversion_dialog: () => void;
}

const GuestConversionContext = createContext<GuestConversionContextType | undefined>(undefined);

const OAUTH_PROVIDERS = [
  { provider: 'github' as const, icon: <GithubLogo className="h-5 w-5" aria-hidden />, title: 'GitHub' },
  { provider: 'google' as const, icon: <FcGoogle className="h-5 w-5" aria-hidden />, title: 'Google' },
  { provider: 'discord' as const, icon: <FaDiscord className="h-5 w-5 text-[#5865F2]" aria-hidden />, title: 'Discord' },
  { provider: 'spotify' as const, icon: <FaSpotify className="h-5 w-5 text-[#1DB954]" aria-hidden />, title: 'Spotify' },
] as const;

export function GuestConversionProvider({ children }: { children: ReactNode }) {
  const { is_anonymous } = useAuth();
  const [open, set_open] = useState(false);
  const [email, set_email] = useState('');
  const [password, set_password] = useState('');
  const [full_name, set_full_name] = useState('');
  const [submitting, set_submitting] = useState(false);
  const [oauth_loading, set_oauth_loading] = useState(false);

  const open_conversion_dialog = useCallback(() => {
    set_open(true);
  }, []);

  const handle_convert = async () => {
    const trimmed_email = email.trim();
    const trimmed_name = full_name.trim();

    if (!trimmed_email || !password) {
      toast.error('Preencha e-mail e senha.');
      return;
    }
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    set_submitting(true);

    const { error } = await supabase.auth.updateUser({
      email: trimmed_email,
      password,
      data: { full_name: trimmed_name || undefined },
    });

    if (error) {
      toast.error(error.message || 'Erro ao converter conta.');
      set_submitting(false);
      return;
    }

    if (trimmed_name) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('profiles').update({
          full_name: trimmed_name,
          updated_at: new Date().toISOString(),
        }).eq('id', user.id);
      }
    }

    toast.success('Conta criada! Verifique seu e-mail para confirmar.');
    set_open(false);
    set_email('');
    set_password('');
    set_full_name('');
    set_submitting(false);
  };

  const handle_oauth = async (provider: 'github' | 'google' | 'discord' | 'spotify') => {
    set_oauth_loading(true);
    try {
      const { data, error } = await supabase.auth.linkIdentity({
        provider,
        options: {
          redirectTo: window.location.href,
          skipBrowserRedirect: true,
        },
      });
      if (error) {
        toast.error(error.message || 'Erro ao vincular conta.');
        return;
      }
      if (data?.url) {
        const popup = window.open(
          data.url,
          `oauth-link-${provider}`,
          'width=520,height=640,menubar=no,toolbar=no,resizable=yes,scrollbars=yes',
        );
        if (!popup) window.location.assign(data.url);
        else popup.focus();
      }
    } finally {
      set_oauth_loading(false);
    }
  };

  return (
    <GuestConversionContext.Provider value={{ open_conversion_dialog }}>
      {children}

      {is_anonymous && (
        <Dialog open={open} onOpenChange={set_open}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Criar conta</DialogTitle>
              <DialogDescription>
                Seus documentos serão mantidos. Basta preencher os dados abaixo.
              </DialogDescription>
            </DialogHeader>

            {/* Botoes OAuth */}
            <div className="flex items-center justify-center gap-3 pt-1">
              {OAUTH_PROVIDERS.map(({ provider, icon, title }) => (
                <Button
                  key={provider}
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 rounded-xl bg-background/50 border-border/60 hover:bg-background/80 hover:scale-105 active:scale-95 transition-all"
                  disabled={oauth_loading || submitting}
                  onClick={() => void handle_oauth(provider)}
                  title={title}
                >
                  {icon}
                </Button>
              ))}
            </div>

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-background px-3 text-muted-foreground">ou com e-mail</span>
              </div>
            </div>

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void handle_convert();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="guest-name">Nome (opcional)</Label>
                <Input
                  id="guest-name"
                  value={full_name}
                  onChange={(e) => set_full_name(e.target.value)}
                  placeholder="Seu nome"
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="guest-email">E-mail</Label>
                <Input
                  id="guest-email"
                  type="email"
                  value={email}
                  onChange={(e) => set_email(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  maxLength={255}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="guest-password">Senha</Label>
                <Input
                  id="guest-password"
                  type="password"
                  value={password}
                  onChange={(e) => set_password(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  maxLength={72}
                />
              </div>

              <Button type="submit" className="w-full" disabled={submitting || oauth_loading}>
                {submitting ? (
                  <>
                    <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
                    Criando…
                  </>
                ) : (
                  'Criar conta'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </GuestConversionContext.Provider>
  );
}

export function useGuestConversion(): GuestConversionContextType {
  const context = useContext(GuestConversionContext);
  if (!context) throw new Error('useGuestConversion must be used within GuestConversionProvider');
  return context;
}
